/** The Layers sheet — Symbology v2's seven groups, generated entirely from
 * src/map/tables/symbology-table.json (the same file that paints the map;
 * see map/symbology.ts's header for why no swatch has a hex of its own any
 * more).
 *
 * Three things changed from the v1 panel this replaces:
 *
 * 1. Rows, groups, labels, defaults and legend art all come from the table.
 *    The old hand-typed ROWS array carried five hexes the map did not
 *    paint; there is nothing left here to mistype.
 * 2. Every group header is a tri-state master over the rows BENEATH IT AS
 *    RENDERED — a group whose unavailable rows were filtered out reads "on"
 *    when everything the rider can see is on, because a master switch that
 *    describes hidden rows is a master switch that lies.
 * 3. Four presets write all 43 rows (including the runtime overlays), light
 *    up when the current state matches, fall back to "Custom" the moment a
 *    row is touched, and leave one tap of Undo behind. v1's Map-mode tiles
 *    set eight switches, left seven alone, showed no selected state and had
 *    no way back.
 *
 * Availability is asked of the STYLE, not of a `requires` string: the panel
 * builds the same style MapCanvas is about to show and checks whether any
 * of a row's layers survived. That catches both gates at once — a state
 * whose manifest never carried the data, and a look that strips it (every
 * `own-*` layer is removed over Satellite and 1950s Topo, where v1 left the
 * ownership switch sitting there doing nothing). Toggles stay instant: no
 * debounce, no confirm, straight through to onChange. */
import { useEffect, useMemo, useRef, useState } from "react";
import type { StateManifest } from "../catalog";
import {
  buildStyle,
  detectPreset,
  applyPreset,
  groupStateFor,
  rowGate,
  setGroup,
  PRESETS,
  PRESET_ORDER,
  SYMBOLOGY_GROUPS,
  SYMBOLOGY_ROWS,
  WEB_RUNTIME_OVERLAYS,
  type GroupState,
  type LayerToggles,
  type MapLook,
  type SymbologyRow,
  type ToggleKey,
} from "../map/firstlight";
import { groundForLook, legendRows, type LegendRow, type Swatch } from "../map/symbology";

/** Why a row can't be switched here. "state" is v1's own wording, kept
 * verbatim; "look" is the case v1 had no words for at all. */
type Unavailable = "state" | "look" | null;

const UNAVAILABLE_CAPTION: Record<"state" | "look", string> = {
  state: "Not available in this state yet",
  look: "Not available on this map",
};

// ----------------------------------------------------------- swatch art

/** Draws a legend entry at its z14 width, with its casing and its dash —
 * the three channels Symbology v2 separates the network by, so a swatch
 * that showed only the colour would be describing a third of the drawing.
 * Dash numbers are in LINE-WIDTH units (MapLibre's own convention, which
 * the class table inherits), so they are multiplied by the width here
 * exactly as the renderer multiplies them. */
function LegendSwatch({ swatch }: { swatch: Swatch }) {
  const W = 44;
  const H = 20;
  const mid = H / 2;

  if (swatch.kind === "fill") {
    return (
      <span
        className="legend-swatch legend-swatch-fill"
        style={{ background: swatch.color }}
        aria-hidden="true"
      />
    );
  }

  if (swatch.kind === "dot") {
    return (
      <svg className="legend-swatch" viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
        <circle
          cx={W / 2}
          cy={mid}
          r={5}
          fill={swatch.color}
          stroke={swatch.stroke ?? "none"}
          strokeWidth={swatch.stroke ? 1.4 : 0}
        />
      </svg>
    );
  }

  const w = swatch.width;
  const dash = swatch.dash ? swatch.dash.map((d) => d * w).join(" ") : undefined;
  const cap = swatch.cap === "butt" ? "butt" : "round";
  const casing = swatch.casing;
  // Two-tone (imagery): sleeve outside core outside fill, each rim counted
  // on BOTH sides of the line, the same arithmetic applyLook uses when it
  // builds the sleeve layer.
  const twoTone = casing !== null && "core" in casing;
  const coreWidth = casing === null ? 0 : "core" in casing ? w + 2 * casing.coreRimPt : w + 2 * casing.rimPt;
  const sleeveWidth =
    casing !== null && "core" in casing ? coreWidth + 2 * casing.sleeveRimPt : 0;

  return (
    <svg className="legend-swatch" viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
      {twoTone && casing !== null && "core" in casing && (
        <line
          x1="1"
          y1={mid}
          x2={W - 1}
          y2={mid}
          stroke={casing.sleeve}
          strokeWidth={sleeveWidth}
          strokeLinecap={cap}
        />
      )}
      {casing !== null && (
        <line
          x1="1"
          y1={mid}
          x2={W - 1}
          y2={mid}
          stroke={"core" in casing ? casing.core : casing.hex}
          strokeWidth={coreWidth}
          strokeLinecap={cap}
        />
      )}
      <line
        x1="1"
        y1={mid}
        x2={W - 1}
        y2={mid}
        stroke={swatch.color}
        strokeWidth={w}
        strokeLinecap={cap}
        strokeDasharray={dash}
        opacity={swatch.opacity}
      />
    </svg>
  );
}

// -------------------------------------------------------- tri-state box

/** `indeterminate` is a DOM property, not an attribute — React has no prop
 * for it, so it has to be written to the node after every render (the
 * standard escape hatch, same shape MDN documents). aria-checked carries
 * the same three states to a screen reader, which `indeterminate` alone
 * does not. */
function TriStateCheckbox({
  state,
  onChange,
  label,
}: {
  state: GroupState;
  onChange: (next: boolean) => void;
  label: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === "mixed";
  }, [state]);
  return (
    <input
      type="checkbox"
      ref={ref}
      checked={state === "on"}
      aria-checked={state === "mixed" ? "mixed" : state === "on"}
      aria-label={label}
      // Mixed and off both mean "there is something here not yet on", so
      // both go fully on; only a fully-on group switches off.
      onChange={() => onChange(state !== "on")}
    />
  );
}

// ------------------------------------------------------------- the sheet

export default function LayersPanel({
  toggles,
  manifest,
  look,
  night,
  onChange,
  onClose,
}: {
  toggles: LayerToggles;
  manifest: StateManifest;
  /** The look the map is showing, so the legend paints the ground the
   * rider is actually looking at and the availability check sees the same
   * removals applyLook is about to make. */
  look: MapLook;
  night: boolean;
  onChange: (t: LayerToggles) => void;
  onClose: () => void;
}) {
  // The style the map is about to draw, and the same state on Outdoors —
  // the pair is what tells a manifest gap ("this state has no parcels")
  // apart from a look gate ("Satellite strips ownership"). Both are
  // memoized: buildStyle is pure and cheap, but it is not free, and this
  // sheet re-renders on every switch flip.
  const liveIds = useMemo(
    () => new Set(buildStyle(manifest, { look, night }).layers.map((l) => String(l.id))),
    [manifest, look, night],
  );
  const baselineIds = useMemo(
    () => new Set(buildStyle(manifest, { look: "outdoors", night: false }).layers.map((l) => String(l.id))),
    [manifest],
  );

  const unavailable = (row: SymbologyRow): Unavailable => rowGate(row, liveIds, baselineIds);

  /** Rows this panel renders at all: every baked row, plus only those
   * runtime overlays the planner implements (WEB_RUNTIME_OVERLAYS — the
   * phone's mud/wiki/past-trips/coverage/hike-mode have no web half, and a
   * permanently dead switch is worse than no switch). They stay in
   * `toggles` either way so presets mean the same thing on both. */
  const shownRows = useMemo(
    () => SYMBOLOGY_ROWS.filter((r) => !r.runtimeOverlay || WEB_RUNTIME_OVERLAYS.has(r.key)),
    [],
  );

  const ground = groundForLook(look, night);
  /** legend entry -> the row whose switch turns it off, so each row can
   * draw its own art without walking the 27-entry list per row. */
  const legendByToggle = useMemo(() => {
    const byToggle = new Map<ToggleKey, LegendRow[]>();
    for (const entry of legendRows(ground, SYMBOLOGY_ROWS)) {
      const list = byToggle.get(entry.toggle);
      if (list) list.push(entry);
      else byToggle.set(entry.toggle, [entry]);
    }
    return byToggle;
  }, [ground]);

  const preset = detectPreset(toggles);
  // The set in force before the last preset tap — one tap of Undo, the
  // thing v1's Map-mode tiles never had. Local to the sheet on purpose:
  // "undo that preset I just picked" is a within-session gesture, and the
  // sheet unmounts when it closes.
  const [undoState, setUndoState] = useState<LayerToggles | null>(null);

  const pickPreset = (name: (typeof PRESET_ORDER)[number]) => {
    setUndoState(toggles);
    onChange(applyPreset(name));
  };

  const firstChipRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    // Same "focus the first reachable control on open" contract as every
    // other sheet (PlansSheet's newRouteRef, SearchPalette's inputRef).
    // Under v2 that control is the first preset chip rather than the first
    // available row's checkbox — the chips are the top of the sheet and
    // are never unavailable.
    firstChipRef.current?.focus();
  }, []);

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div
        className="sheet layers-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="layers-heading"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            // stopPropagation is load-bearing here, not defensive
            // decoration: LAYERS is reachable mid-draw (unlike PLAN/STATE),
            // so without it this Escape would ALSO reach App's window-level
            // draw-mode handler and deselect a point or open the discard
            // prompt in the same keystroke.
            e.stopPropagation();
            onClose();
          }
        }}
      >
        <h2 id="layers-heading">Layers</h2>

        <div className="preset-chips" role="group" aria-label="Presets">
          {PRESET_ORDER.map((name, i) => {
            const info = PRESETS[name];
            const isCurrent = preset === name;
            return (
              <button
                key={name}
                ref={i === 0 ? firstChipRef : undefined}
                type="button"
                className={isCurrent ? "preset-chip current" : "preset-chip"}
                aria-pressed={isCurrent}
                title={info.blurb}
                onClick={() => pickPreset(name)}
              >
                {info.label}
              </button>
            );
          })}
          {preset === "custom" && (
            <span className="preset-chip preset-chip-custom" aria-live="polite">
              Custom
            </span>
          )}
          {undoState !== null && (
            <button
              type="button"
              className="preset-chip preset-chip-undo"
              title="Put every layer back the way it was before the last preset"
              onClick={() => {
                onChange(undoState);
                setUndoState(null);
              }}
            >
              Undo
            </button>
          )}
        </div>
        <p className="preset-blurb">
          {preset === "custom" ? "Your own mix." : PRESETS[preset].blurb}
        </p>

        {SYMBOLOGY_GROUPS.map((group) => {
          const rows = shownRows.filter((r) => r.group === group.id);
          if (rows.length === 0) return null;
          // The master switch describes only the rows a rider can actually
          // flip — see this file's header, point 2.
          const switchable = rows.filter((r) => unavailable(r) === null).map((r) => r.key);
          const state = groupStateFor(switchable, toggles);
          return (
            <section className="layers-group" key={group.id}>
              <div className="layers-group-head">
                <h3 className="layers-group-title">{group.label}</h3>
                <TriStateCheckbox
                  state={state}
                  label={`${group.label} — all`}
                  onChange={(next) => onChange(setGroup(switchable, toggles, next))}
                />
              </div>
              <ul className="picker-list">
                {rows.map((row) => {
                  const gate = unavailable(row);
                  const entries = legendByToggle.get(row.key) ?? [];
                  // One legend entry rides inline on the row; two or more
                  // (paved highway / paved road / service lane, or the two
                  // contour weights) get their own lines beneath it, because
                  // stacking three swatches in one gutter reads as one
                  // smeared line.
                  const inline = entries.length === 1 ? entries[0] : null;
                  return (
                    <li key={row.key}>
                      <label className={gate === null ? "row" : "row row-disabled"}>
                        <span className="row-main">
                          {inline && <LegendSwatch swatch={inline.swatch} />}
                          <span className="row-text">
                            <span>{row.label}</span>
                            {inline?.blurb && <span className="row-footnote">{inline.blurb}</span>}
                          </span>
                        </span>
                        {gate === null ? (
                          <input
                            type="checkbox"
                            checked={toggles[row.key]}
                            onChange={(e) =>
                              onChange({ ...toggles, [row.key]: e.target.checked })
                            }
                          />
                        ) : (
                          <span className="row-caption">{UNAVAILABLE_CAPTION[gate]}</span>
                        )}
                      </label>
                      {entries.length > 1 && (
                        <ul className="legend-lines">
                          {entries.map((entry) => (
                            <li className="legend-line" key={entry.id}>
                              <LegendSwatch swatch={entry.swatch} />
                              <span className="row-text">
                                <span>{entry.label}</span>
                                {entry.blurb && <span className="row-footnote">{entry.blurb}</span>}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </div>
    </div>
  );
}
